import { PdfHtml } from '../../../common/components';
import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View
} from '@react-pdf/renderer';
import { PtgFeaturesContextType } from './PtgFeaturesContextType';
import {
  EFlyerPDF,
  QRCallToActionPdf,
  EFlyerFooterPDF,
  useHydrateFooter
} from '../../common';

/** @typedef {import('./PtgFeaturesContextType').PtgFeaturesContext} PtgFeaturesContext */

export const PtgFeaturesPdf = ({ context }) => {
  const footerData = useHydrateFooter(context.footer);

  if (!footerData.ready) {
    return null;
  }

  // The PDF will not be re-rendered, so ensure all data is available before rendering
  // If qrUrl is provided, wait for qr to be generated
  if (context.qrUrl && !context.qrImage) {
    return null;
  }

  // disable hyphenation
  Font.registerHyphenationCallback(word => [word]);

  return (
    <EFlyerPDF>
      <PTGFeatures context={context} footerData={footerData} />
    </EFlyerPDF>
  );
};

PtgFeaturesPdf.propTypes = {
  context: PtgFeaturesContextType.isRequired
};

/**
 * Renders the PDF document for the Features template
 * @param {{context: PtgFeaturesContext}} props
 * @returns {React.JSX.Element}
 */
const PTGFeatures = ({ context, footerData }) => {
  const { themeColors } = context;
  const styles = StyleSheet.create({
    page: {
      padding: 10
    },
    headerContainer: {
      backgroundColor: '#FFFFFF',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: 12,
      paddingBottom: 12,
      height: '12%',
      borderBottom: '5px solid ' + themeColors.color500
    },
    logoContainer: {
      flexGrow: 0,
      flexShrink: 0
    },
    logo: {
      height: '95%',
      width: 'auto',
      objectFit: 'contain',
      marginLeft: 5
    },
    headings: {
      flexGrow: 1,
      alignItems: 'flex-end',
      marginLeft: 20
    },
    heading1: {
      fontSize: 18,
      lineHeight: '24pt',
      color: themeColors.color500,
      textAlign: 'right'
    },
    heading2: {
      fontSize: 18,
      color: themeColors.color500,
      fontWeight: 'bold',
      textAlign: 'right'
    },
    mainContainer: {
      paddingLeft: 50,
      paddingRight: 50,
      height: '61%'
    },
    title: {
      color: themeColors.color600,
      fontSize: 26,
      fontWeight: 'bold',
      marginTop: 20,
      marginBottom: 20
    },
    contentRow: {
      display: 'flex',
      flexDirection: 'row'
    },
    imageContainer: {
      width: '50%',
      paddingLeft: 10,
      paddingRight: 40
    },
    mainImage: {
      maxHeight: 200,
      objectFit: 'contain',
      objectPosition: 'left'
    },
    paragraph: {
      width: '50%',
      color: '#2d3748'
    },
    featuresHeading: {
      color: '#000000',
      fontSize: 15,
      fontWeight: 'bold',
      marginTop: 30,
      marginBottom: 5
    },
    featuresList: {
      fontSize: 15,
      lineHeight: '18pt'
    },
    featureListItem: {
      display: 'flex',
      flexDirection: 'row'
    },
    feature: {},
    bullet: {
      fontSize: 14,
      color: '#000000',
      paddingRight: 20
    },
    footerContainer: {
      backgroundColor: themeColors.color500,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: '12%'
    }
  });

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.headerContainer}>
          <View style={styles.logoContainer}>
            <Image src={context.logoUrl} style={styles.logo} />
          </View>
          <View style={styles.headings}>
            <Text style={styles.heading1}>{context.heading1}</Text>
            <Text style={styles.heading2}>{context.heading2}</Text>
          </View>
        </View>
        <View style={styles.mainContainer}>
          <Text style={styles.title}>{context.title}</Text>
          <View style={styles.contentRow}>
            <View style={styles.imageContainer}>
              <Image src={context.mainImageUrl} style={styles.mainImage} />
            </View>
            <PdfHtml
              stylesheet={{
                '*': {
                  marginTop: 0
                }
              }}
              style={styles.paragraph}
            >
              {context.paragraph}
            </PdfHtml>
          </View>
          <View>
            <Text style={styles.featuresHeading}>
              {context.featuresHeading}
            </Text>
            <View style={styles.featuresList}>
              {Array.isArray(context.features) && context.features.length > 0
                ? context.features.map((feature, idx) => (
                    <View style={styles.featureListItem} key={idx}>
                      <Text style={styles.bullet}>•</Text>
                      <Text style={styles.feature}>{feature}</Text>
                    </View>
                  ))
                : null}
            </View>
          </View>
        </View>
        <QRCallToActionPdf
          callToAction={context.callToAction}
          qrUrl={context.qrUrl}
          qrImage={context.qrImage}
        />
        <View style={styles.footerContainer}>
          <EFlyerFooterPDF footerData={footerData} />
        </View>
      </Page>
    </Document>
  );
};
